"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type Ref,
} from "react";
import { useTranslation } from "react-i18next";
import {
  MaximumMortgageWizard,
  type MaximumMortgageWizardHandle,
  type MaximumMortgageWizardFormValues,
  type MaximumMortgageWizardState,
  type MaximumMortgageFlowConfig,
  useMaximumMortgageCookie,
  formValuesToStringParams,
} from "@huub-web/shared";
import {
  type FormFieldSchema,
  type UINavigateAction,
  useActions,
  useDebug,
  usePubSub,
} from "@huub-web/ui";

import { useTemplates } from "../../hooks/useTemplates";
import { useInputUpdate } from "../../hooks/useInputUpdate";
import {
  useAgentFormAwareness,
  type WizardStepInfo,
} from "../../hooks/useAgentFormAwareness";

export type MaximumMortgageWizardTemplateProps = {
  /**
   * Unique identifier for this template instance
   */
  id: string;
  /**
   * API endpoint for address search autocomplete queries.
   * Query string is appended as `?query=<encoded>`.
   */
  searchEndpoint?: string;
  /**
   * API endpoint for fetching a listing by address object ID.
   * Must contain `[id]` placeholder that gets replaced at runtime.
   */
  listingEndpoint?: string;
  /**
   * Navigation context passed to the navigate action on form submission
   */
  submitContext: UINavigateAction["payload"]["context"];
  /**
   * Backend-driven flow configuration embedded in the template data.
   * When present, restricts which mortgage types are available.
   */
  flowConfig?: MaximumMortgageFlowConfig;
  /**
   * Endpoint for saving and calculating mortgage (authenticated flow).
   * When provided, the wizard POSTs form values to this endpoint.
   * When absent, falls back to legacy query-param navigation.
   */
  calculateEndpoint?: string;
  /**
   * API endpoint that returns the list of banks/lenders.
   * When provided, the lender dropdown in the wizard is populated from this endpoint.
   * Expected to return a `{ banks: Bank[] }` JSON response.
   */
  banksEndpoint?: string;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * Template component that wraps the MaximumMortgageWizard with API integration,
 * cookie-based pre-fill, and form state reporting.
 */
export const MaximumMortgageWizardTemplate: FC<
  MaximumMortgageWizardTemplateProps
> = ({
  id,
  searchEndpoint,
  listingEndpoint,
  submitContext = ["maximumMortgage", "result"],
  flowConfig,
  calculateEndpoint,
  banksEndpoint,
  ref,
}) => {
  const { $get, $post, env } = useTemplates();
  const { handleAction } = useActions();
  const { log } = useDebug();
  const pubSub = usePubSub();
  const { getMaximumMortgageCookie, deleteMaximumMortgageCookie } =
    useMaximumMortgageCookie({
      cookieName: env.cookieName,
      cookieDomain: env.cookieDomain,
    });

  // Read cookie synchronously on first render to get initial values
  // This must happen BEFORE the wizard mounts to avoid race conditions
  const [initialValues] = useState<
    Partial<MaximumMortgageWizardFormValues> | undefined
  >(() => {
    const cookieData = getMaximumMortgageCookie();
    return cookieData ?? undefined;
  });

  const hasClearedCookieRef = useRef(false);

  useEffect(() => {
    log("purple", ["MaximumMortgageWizardTemplate", "flowConfig"], flowConfig);
  }, [flowConfig, log]);

  // Clear the cookie after reading (only once)
  useEffect(() => {
    if (hasClearedCookieRef.current || !initialValues) {
      return;
    }

    hasClearedCookieRef.current = true;

    log(
      "purple",
      ["MaximumMortgageWizardTemplate", "cookieFound"],
      initialValues,
    );

    deleteMaximumMortgageCookie();
  }, [initialValues, deleteMaximumMortgageCookie, log]);

  // Form state reporting
  // --------------------

  const wizardStateRef = useRef<MaximumMortgageWizardState | null>(null);
  const [wizardFormValues, setWizardFormValues] = useState<
    Record<string, unknown>
  >({});
  const [currentSlide, setCurrentSlide] = useState<string | null>(null);

  const { t } = useTranslation();

  /**
   * Per-slide field schema registry.
   * Each BaseSlide fires onFieldSchemaChange with only its own fields,
   * so we accumulate schemas per slide key and derive a merged array.
   */
  const slideSchemaMapRef = useRef<Map<string, FormFieldSchema[]>>(new Map());
  const [slideSchemaVersion, setSlideSchemaVersion] = useState(0);

  /**
   * Build wizard step metadata from i18n translations.
   * Uses hasPartner to determine singular/plural phrasing.
   */
  /**
   * Only depends on `hasPartner` (a boolean primitive) instead of the full
   * `wizardFormValues` object, which gets a new reference on every keystroke.
   * `currentSlide` is included so the memo re-runs when the wizard
   * initialises (ref goes from null → populated in the same handler).
   */
  const hasPartner = wizardFormValues?.hasPartner ?? false;
  const wizardStepInfo = useMemo((): WizardStepInfo[] | undefined => {
    const state = wizardStateRef.current;
    if (!state) return undefined;

    const plurality = hasPartner ? "plural" : "singular";
    return [
      {
        key: "income",
        title: t("maxMortgageWizard.income.title"),
        description:
          "Inkomen, leeftijd, optioneel partner met inkomen en leeftijd",
      },
      {
        key: "purpose",
        title: t(`maxMortgageWizard.purpose.title.${plurality}`),
        description:
          "Hypotheekdoel (financieren/oversluiten/verbouwen). Bij financieren: huis op het oog? Bij bestaande bouw: hoever met aankoop?",
      },
      {
        key: "newHouse",
        title: t(`maxMortgageWizard.newHouse.title.${plurality}`),
        description:
          "Vraagprijs en energielabel. Verschijnt alleen bij financieren + huis op het oog.",
        conditional: true,
      },
      {
        key: "currentHouse",
        title: t(`maxMortgageWizard.currentHouse.title.${plurality}`),
        description:
          "Bij ja: verwachte verkoopprijs en resterende hypotheekschuld. Verschijnt alleen bij financieren.",
        conditional: true,
      },
      {
        key: "financialObligations",
        title: t(`maxMortgageWizard.financialObligations.title.${plurality}`),
        description:
          "Leningen, studieschuld, private lease (elk optioneel met maandlasten per persoon). Eigen vermogen.",
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, hasPartner, currentSlide]);

  /**
   * Memoised wizard metadata — only changes when the current slide
   * or the step definitions change, preventing the debounce timer
   * in useAgentFormAwareness from resetting on unrelated re-renders.
   */
  const wizardParam = useMemo(() => {
    if (!currentSlide || !wizardStepInfo) return undefined;

    return {
      currentStep: currentSlide,
      currentStepIndex: Math.max(
        0,
        wizardStepInfo.findIndex((s) => s.key === currentSlide),
      ),
      steps: wizardStepInfo,
      totalSteps: wizardStepInfo.length,
    };
  }, [currentSlide, wizardStepInfo]);

  /**
   * Merged field schema across all slides, derived from the per-slide
   * registry. Slides are ordered by their position in wizardStepInfo so
   * the agent sees panels in the correct sequence.
   */
  const mergedFieldSchema = useMemo((): FormFieldSchema[] => {
    if (slideSchemaMapRef.current.size === 0) return [];

    if (wizardStepInfo) {
      return wizardStepInfo.flatMap(
        (step) => slideSchemaMapRef.current.get(step.key) ?? [],
      );
    }

    return Array.from(slideSchemaMapRef.current.values()).flat();
    // slideSchemaVersion is intentionally the only reactive trigger here —
    // the map itself is a stable ref mutated in handleFieldSchemaChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideSchemaVersion, wizardStepInfo]);

  useAgentFormAwareness({
    formId: id,
    formType: "wizard",
    fieldSchema: mergedFieldSchema,
    wizard: wizardParam,
  });

  /**
   * Receives the enriched field schema from Form.onSchemaChange via
   * BaseSlide → wizard context → template. Contains fieldKey, label,
   * type, step, value — everything the AI agent needs.
   *
   * Each BaseSlide reports only its own fields; we accumulate per slide key
   * and merge into a single array so useAgentFormAwareness sees all panels.
   */
  const handleFieldSchemaChange = useCallback(
    (slideKey: string, schema: FormFieldSchema[]) => {
      slideSchemaMapRef.current.set(slideKey, schema);
      setSlideSchemaVersion((v) => v + 1);
    },
    [],
  );

  // inputUpdate subscription
  // -----------------------

  const wizardRef = useRef<MaximumMortgageWizardHandle>(null);

  useInputUpdate({
    prefix: `${id}__`,
    debugLabel: "MaximumMortgageWizardTemplate",
    onUpdate: useCallback((updates: Record<string, unknown>) => {
      if ("nextStep" in updates) {
        const { nextStep: _, ...fieldUpdates } = updates;
        if (Object.keys(fieldUpdates).length > 0) {
          wizardRef.current?.updateFormValues(
            fieldUpdates as Partial<MaximumMortgageWizardFormValues>,
          );
          // Defer navigation to the next macrotask so React can process
          // the dispatch from updateFormValues. Without this, navigateToNext
          // reads stale pre-update form values on the last slide, causing
          // the submission to use old field values.
          setTimeout(() => wizardRef.current?.navigateToNext(), 0);
        } else {
          // No field updates — navigate immediately.
          wizardRef.current?.navigateToNext();
        }
        return;
      }
      if ("previousStep" in updates) {
        wizardRef.current?.navigateToPrevious();
        return;
      }
      wizardRef.current?.updateFormValues(
        updates as Partial<MaximumMortgageWizardFormValues>,
      );
    }, []),
  });

  // API handlers
  // ------------

  const getSearchResults = useCallback(
    async (query: string) => {
      if (!searchEndpoint) {
        console.warn(
          "[MaximumMortgageWizardTemplate] No search endpoint provided",
        );
        return [];
      }

      const url = `${searchEndpoint}?query=${encodeURIComponent(query)}`;

      return $get(url).then((data) => data?.data?.results ?? []);
    },
    [searchEndpoint, $get],
  );

  const getListing = useCallback(
    async (addressObjectID: string) => {
      if (!listingEndpoint) {
        console.warn(
          "[MaximumMortgageWizardTemplate] No listing endpoint provided",
        );
        return null;
      }

      const url = listingEndpoint.replace("[id]", addressObjectID);

      return $get(url).then((data) => data?.data ?? null);
    },
    [listingEndpoint, $get],
  );

  const getBanks = useCallback(async () => {
    if (!banksEndpoint) return [];
    return $get(banksEndpoint).then((data) => data?.data?.banks ?? []);
  }, [banksEndpoint, $get]);

  // Event handlers
  // -------------

  const handleStateChange = useCallback(
    (state: MaximumMortgageWizardState) => {
      log("purple", ["MaximumMortgageWizardTemplate", "stateChange"], state);
      wizardStateRef.current = state;
      setWizardFormValues(state.formValues as Record<string, unknown>);
      setCurrentSlide(state.currentSlide);
    },
    [log],
  );

  const handleSubmit = useCallback(
    async (formValues: Partial<MaximumMortgageWizardFormValues>) => {
      if (calculateEndpoint) {
        try {
          const body = Object.fromEntries(
            Object.entries(formValues).filter(([, v]) => v !== undefined),
          );
          const response = await $post(calculateEndpoint, body);
          if (response?.data?.success && response?.data?.itemId) {
            handleAction({
              type: "navigate",
              payload: {
                context: submitContext,
                params: { id: response.data.itemId },
              },
            });
          } else {
            log(
              "red",
              "MaximumMortgageWizardTemplate",
              "Calculate failed",
              response,
            );
            pubSub.publish("snackbar", {
              variant: "error",
              body: t("templating.maximumMortgageWizard.saveError"),
            });
          }
        } catch (error) {
          log("red", "MaximumMortgageWizardTemplate", "Calculate error", error);
          pubSub.publish("snackbar", {
            variant: "error",
            body: t("templating.maximumMortgageWizard.saveError"),
          });
        }
      } else {
        handleAction({
          type: "navigate",
          payload: {
            context: submitContext,
            params: formValuesToStringParams(formValues),
          },
        });
      }
    },
    [handleAction, submitContext, calculateEndpoint, $post, log, pubSub, t],
  );

  return (
    <MaximumMortgageWizard
      ref={ref}
      wizardRef={wizardRef}
      initialValues={initialValues}
      getSearchResults={getSearchResults}
      getListing={getListing}
      getBanks={getBanks}
      onSubmit={handleSubmit}
      onStateChange={handleStateChange}
      onFieldSchemaChange={handleFieldSchemaChange}
      flowConfig={flowConfig}
    />
  );
};
